// src/core/composables/useNotificationsApi.ts
import { ref } from "vue";
import type { PagedResponse } from "@/models";
import { useAlerts } from "@/shared/composables/useAlerts";
import { createApiComposable } from "@/core/composables/createApiComposable";
import type { Notification, NotificationSearchCriteria } from "@/features/notifications/types";
import { notificationsService } from "@/core/services/notifications.service";
import { useAppContextStore } from "../store/AppContextStore";
import { usePermissions } from "./usePermissions";
import { Permissions } from "@/features/permissions/types/permissions";

type UseNotificationsApiEvents = {
  onNotificationsChanged?: () => void;
};

export function useNotificationsApi(events: UseNotificationsApiEvents = {}) {
  const notification = ref<Notification>({} as Notification);
  const notifications = ref<PagedResponse<Notification>>({} as PagedResponse<Notification>);
  const notificationsExport = ref<Notification[]>([]);
  const activeNotification = ref<Notification>({} as Notification);

  const userContext = useAppContextStore();
  const perms = usePermissions(() => userContext.context.user);

  const { errorToast, successToast } = useAlerts();
  const api = createApiComposable({
    errorToast,
    successToast,
    defaultErrorMessage: "Request failed",
  });

  const fetchNotifications = async (criteria: NotificationSearchCriteria) => {
    const data = await api.run(
      () => notificationsService.fetchNotifications(criteria),
      "Failed to load notifications"
    );

    notifications.value = data;
    activeNotification.value = data.items?.length ? data.items[0] : ({} as Notification);
    return data;
  };

  const fetchNotificationsExport = async (criteria: NotificationSearchCriteria) => {
    const data = await api.run(
      () => notificationsService.fetchNotificationsExport(criteria),
      "Failed to load notifications"
    );

    notificationsExport.value = data;
    activeNotification.value = data.length ? data[0] : ({} as Notification);
    return data;
  };

  const fetchNotificationsById = async (id: number) => {
    const data = await api.run(
      () => notificationsService.fetchNotificationById(id),
      "Failed to load notification"
    );
    notification.value = data;
    return data;
  };

  const addNotification = async (newNotification: Notification) => {
    if (!perms.hasPermission(Permissions.NotificationsPage).value) {
      return null;
    }

    const data = await api.runWithSuccess(
      () => notificationsService.addNotification(newNotification),
      "Saved",
      "Notification saved",
      "Failed to add notification"
    );
    notification.value = data;
    events.onNotificationsChanged?.();
    return data;
  };

  const updateNotification = async (updatedNotification: Notification) => {
    if (!perms.hasPermission(Permissions.NotificationsPage).value) {
      return null;
    }

    const data = await api.runWithSuccess(
      () => notificationsService.updateNotification(updatedNotification),
      "Saved",
      "Notification saved",
      "Failed to update notification"
    );
    notification.value = data;
    events.onNotificationsChanged?.();
    return data;
  };

  const deleteNotification = async (id: number) => {
    if (!perms.hasPermission(Permissions.NotificationsPage).value) {
      return null;
    }

    await api.runVoidWithSuccess(
      () => notificationsService.deleteNotification(id),
      "Saved",
      "Notification deleted",
      "Failed to delete notification"
    );

    events.onNotificationsChanged?.();

    // optional: clear local current notification if it was deleted
    if (notification.value?.id === id) notification.value = {} as Notification;
  };

  return {
    activeNotification,
    notifications,
    notificationsExport,
    notification,

    // shared api state
    loading: api.loading,
    error: api.error,
    clearError: api.clearError,

    fetchNotifications,
    fetchNotificationsExport,
    fetchNotificationsById,
    addNotification,
    updateNotification,
    deleteNotification,
  };
}
